/**
 * Vistoera Canvas HTML5 Drag Contract
 * (VISTOERA_CANVAS_INTERNAL_TEXT_NATIVE_DRAG_LEAK_AND_GHOST_ELEMENTS_P0_V1)
 *
 * ALLOWLIST ONLY — blank Canvas / React Flow pane must never create nodes or
 * overlays from browser-native `text/plain` / `text/html` drags.
 *
 * Reuses existing canonical MIME strings already used by palette / creative /
 * script / asset-mention flows — do not invent parallel names.
 */

/** Palette → Canvas create node (LeftLibraryPanel → CanvasBoard.onDrop). */
export const PCV_NODE_TYPE_DND_MIME = 'application/pcv-node-type'

/**
 * Image Asset Picker thumbnail → Canvas place at drop position
 * (VistoeraTASK-00591). Payload is picker-item reference only — never a
 * downloaded original / Blob / Base64. Resolve happens once on drop via
 * the existing `resolveImagePickerSelection` + `materializeImageAssetSelections`
 * path.
 */
export const PCV_IMAGE_ASSET_DND_MIME = 'application/pcv-image-asset'

/** Body class toggled while an image-picker HTML5 drag is active so the
 * Ant Design Modal mask can pass events through to the canvas. */
export const IAP_IMAGE_ASSET_DRAGGING_BODY_CLASS = 'iap-image-asset-dragging'

/** Creative Idea → Script node context linking. */
export const PCV_CREATIVE_CONTEXT_DND_MIME = 'application/x-pcv-creative-context'

/** Script Artifact → Storyboard context linking. */
export const PCV_SCRIPT_CONTEXT_DND_MIME = 'application/x-pcv-script-context'

/** @deprecated Storyboard→standalone Video Merge DnD removed; MIME kept only if any residual listeners.
 * (VISTOERA_VIDEO_PRODUCTION_ROUGH_CUT_TIMELINE_PREVIEW_AND_FINAL_
 * ASSEMBLY_CARD_P0_V1 — same "Quick Action and drag share one payload
 * contract" convention as the Script→Storyboard MIME above). */
export const PCV_STORYBOARD_CONTEXT_DND_MIME = 'application/x-pcv-storyboard-context'

/** Input material chip → prompt mention editor. */
export const PCV_ASSET_MENTION_DND_MIME = 'application/x-pcv-asset-mention'

/** MIME types the React Flow *pane* may accept for creating / placing nodes. */
export const CANVAS_PANE_DROP_MIME_ALLOWLIST = [
  PCV_NODE_TYPE_DND_MIME,
  PCV_IMAGE_ASSET_DND_MIME,
] as const

/** All first-party Vistoera canvas HTML5 payloads (pane + node-to-node). */
export const VISTOERA_CANVAS_DND_MIME_ALLOWLIST = [
  PCV_NODE_TYPE_DND_MIME,
  PCV_IMAGE_ASSET_DND_MIME,
  PCV_CREATIVE_CONTEXT_DND_MIME,
  PCV_SCRIPT_CONTEXT_DND_MIME,
  PCV_STORYBOARD_CONTEXT_DND_MIME,
  PCV_ASSET_MENTION_DND_MIME,
] as const

export type VistoeraCanvasDndKind =
  | 'palette-node'
  | 'image-asset'
  | 'creative-context'
  | 'script-context'
  | 'storyboard-context'
  | 'asset-mention'

/** Lightweight drag payload — picker item id + display hints only. */
export type PcvImageAssetDragPayload = {
  pickerItemId: string
  filename?: string | null
  thumbnailUrl?: string | null
}

export function serializeImageAssetDragPayload(payload: PcvImageAssetDragPayload): string {
  return JSON.stringify({
    pickerItemId: payload.pickerItemId,
    filename: payload.filename ?? null,
    thumbnailUrl: payload.thumbnailUrl ?? null,
  })
}

export function parseImageAssetDragPayload(raw: string | null | undefined): PcvImageAssetDragPayload | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PcvImageAssetDragPayload>
    if (typeof parsed.pickerItemId !== 'string' || !parsed.pickerItemId) return null
    return {
      pickerItemId: parsed.pickerItemId,
      filename: typeof parsed.filename === 'string' ? parsed.filename : parsed.filename ?? null,
      thumbnailUrl:
        typeof parsed.thumbnailUrl === 'string' ? parsed.thumbnailUrl : parsed.thumbnailUrl ?? null,
    }
  } catch {
    return null
  }
}

export function setImageAssetDraggingChrome(active: boolean): void {
  if (typeof document === 'undefined') return
  document.body.classList.toggle(IAP_IMAGE_ASSET_DRAGGING_BODY_CLASS, active)
}

/** Mark legal HTML5 drag sources so shell-level native-text guards can skip them. */
export const VISTOERA_DND_ATTR = 'data-vistoera-dnd'

export function vistoeraDndProps(kind: VistoeraCanvasDndKind): Record<string, string> {
  return { [VISTOERA_DND_ATTR]: kind }
}

function typeList(dt: DataTransfer | null | undefined): string[] {
  if (!dt) return []
  try {
    return Array.from(dt.types || [])
  } catch {
    return []
  }
}

export function dataTransferHasMime(dt: DataTransfer | null | undefined, mime: string): boolean {
  return typeList(dt).includes(mime)
}

export function isVistoeraCanvasDrag(dt: DataTransfer | null | undefined): boolean {
  const types = typeList(dt)
  return VISTOERA_CANVAS_DND_MIME_ALLOWLIST.some((m) => types.includes(m))
}

/** React Flow pane: palette node-type or image-asset-picker drops. */
export function isSupportedCanvasPaneDragOver(dt: DataTransfer | null | undefined): boolean {
  return (
    dataTransferHasMime(dt, PCV_NODE_TYPE_DND_MIME) ||
    dataTransferHasMime(dt, PCV_IMAGE_ASSET_DND_MIME)
  )
}

export function isImageAssetPickerDrag(dt: DataTransfer | null | undefined): boolean {
  return dataTransferHasMime(dt, PCV_IMAGE_ASSET_DND_MIME)
}

export function isNativeTextOnlyDrag(dt: DataTransfer | null | undefined): boolean {
  const types = typeList(dt)
  if (types.length === 0) return false
  if (isVistoeraCanvasDrag(dt)) return false
  const onlyBrowserText = types.every(
    (t) =>
      t === 'text/plain' ||
      t === 'text/html' ||
      t === 'text/uri-list' ||
      // Chromium legacy URI list alias
      t === 'Files' ||
      t.startsWith('text/'),
  )
  // Pure text/html (+ optional Files from OS) without our MIME → ignore for pane create.
  return onlyBrowserText || (types.includes('text/plain') && !isVistoeraCanvasDrag(dt))
}

/**
 * Capture-phase guard for Canvas surface: cancel browser-native text/image
 * drags that did not originate from a marked Vistoera DnD source.
 * Legal sources set `data-vistoera-dnd` and call setData in their own
 * onDragStart (target/bubble); capture runs first — allow those elements
 * by attribute, block everything else under the canvas/node chrome.
 */
export function shouldBlockNativeCanvasDragStart(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null
  if (!el) return false
  if (el.closest(`[${VISTOERA_DND_ATTR}]`)) return false
  if (el.closest('input, textarea, select, [contenteditable="true"]')) {
    // Allow caret / selection inside editors; still block dragging the
    // selection *out* onto the canvas by cancelling dragstart here —
    // copy/paste via keyboard remains available.
    return true
  }
  // Only guard inside the canvas / node chrome, not the whole document
  // (so OS file drags into other pages are unaffected).
  return Boolean(el.closest('.react-flow, .pcv-canvas-root, .pcv-node, .react-flow__node'))
}

export function blockNativeCanvasDragStartIfNeeded(e: DragEvent): void {
  if (!shouldBlockNativeCanvasDragStart(e.target)) return
  e.preventDefault()
}
